import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AnalyticsTimelineEvent} from '../../../../../models/api/analyticsTimelineEventModel';
import {CardModel} from '../../../../../models/api/cardModel';
import {MessageModel} from '../../../../../models/api/messageModel';
import {ResourceMap} from '../../../../../store/appTypes';
import {renderHiddenEventsButton, renderTimelineEvent} from './timelineSectionHelpers';

/*
 * Types.
 */

// Represents a section of events in the timeline. Used for handling visibility. The
// events array can contain any number of events (including just 1) as long as
// we want to treat all those events the same from a visiblity standpoint.
export interface TimelineEventsSection {
  key: string;
  events: ReadonlyArray<AnalyticsTimelineEvent>;
}

/*
 * Props.
 */

interface TimelineEventsSectionProps {
  timelineSection: TimelineEventsSection;
  messagesResourcesMap: ResourceMap<MessageModel>;
  cardsResourcesMap: ResourceMap<CardModel>;
  isVisible: boolean;
  isLastSection: boolean;
  showSection: Handler;
}

/*
 * Styles.
 */

const StyledEventWrapperDiv = styled.div`
  display: grid;
  grid-template-rows: 52px auto;
  grid-template-columns: 40px auto;
  grid-template-areas:
    'icon event'
    'link body';
  align-items: center;
`;

const StyledHiddenEventsWrapperDiv = styled.div`
  display: grid;
  grid-template-rows: 30px auto;
  grid-template-columns: 40px auto;
  grid-template-areas:
    'icon button'
    'link body';
  align-items: center;
`;

const StyledEventIconLinkerDiv = styled.div`
  grid-area: link;
  align-self: stretch;

  border-radius: 8px;
  width: 2px;

  // -8px top and bottom so it's closer to the icons, 11px left to align with the middle of the icon as (24px icon width - 2px link width)/2.
  margin: -8px 0 -8px 11px;

  background-color: ${(p) => p.theme.greys.shade40};
`;

/*
 * Component.
 */

export const TimelineEventsSection: FC<TimelineEventsSectionProps> = ({
  timelineSection,
  messagesResourcesMap,
  cardsResourcesMap,
  isVisible,
  isLastSection,
  showSection,
}) => {
  if (isVisible) {
    // Return a fragment containing the events.
    return (
      <>
        {timelineSection.events.map((event, index) => {
          // True if this is the last item of the last sectio - so we don't want an icon linker.
          const isLast = isLastSection && index === timelineSection.events.length - 1;
          return renderTimelineEventWithIconLinker(
            renderTimelineEvent(event, messagesResourcesMap, cardsResourcesMap),
            event.id,
            isLast,
          );
        })}
      </>
    );
  }

  // If this section is not visible, return a button with the number of
  // hidden events that shows them when clicked.
  return renderShowHiddenEventsButtonWithIconLinker(
    timelineSection.key,
    timelineSection.events.length,
    isLastSection,
    showSection,
  );
};

/*
 * Helpers.
 */

export function renderTimelineEventWithIconLinker(eventNode: ReactNode, key: number, isLast: boolean) {
  return (
    <StyledEventWrapperDiv key={key}>
      {eventNode} {/** This could be an actual event or the initial loading skeleton. */}
      {!isLast && <StyledEventIconLinkerDiv />}
    </StyledEventWrapperDiv>
  );
}

function renderShowHiddenEventsButtonWithIconLinker(
  key: string,
  eventsCount: number,
  isLast: boolean,
  onClick: Handler,
) {
  return (
    <StyledHiddenEventsWrapperDiv key={`hidden-events-${key}`}>
      {renderHiddenEventsButton(eventsCount, onClick)}
      {!isLast && <StyledEventIconLinkerDiv />}
    </StyledHiddenEventsWrapperDiv>
  );
}
